import {
  TextField,
  EmailField,
  TextInput,
  BooleanInput,
  PasswordInput,
  useTheme,
  useGetIdentity,
  useRecordContext,
} from "react-admin";
import { Link } from "react-router-dom";
import {
  validateUserCreation,
  validateUserUpdate,
} from "../utils/customValidators";
import { MyBooleanField as BooleanField } from "../components/myBooleanField";

import { RecordSummary, MyShow } from "../ra-lb-tools/components/MyShow";
import { Box, Tab, Tabs } from "@mui/material";
import { useParams } from "react-router-dom";
import { TowerList } from "./tower/towers";
import { ReservationList } from "./reservations";
import { OpenLockerTokenList as OpenLockerTokenList } from "./openLockerTokens";
import get from "lodash/get";
import { Settings } from "../ra-lb-tools/utils/settings";
import { ListHeader, MyList } from "../ra-lb-tools/components/myList";
import { SystemList } from "./systems";
import { LocationList } from "./locations/locations";
import {
  MyEdit as Edit,
  MyCreate as Create,
} from "../ra-lb-tools/components/myMutator";
import { ActiveLockerUsageList, LockerUsageList } from "./billing/lockerUsage";
import { sxSpot } from "../ra-lb-tools/theme/styles";
import { LabeledColumn } from "../ra-lb-tools/components/labeledColumn";
import {
  MyReferenceField,
  MyReferenceInput,
} from "../ra-lb-tools/components/myReference";
import { OrganizationField, OrganizationList } from "./billing/organizations";
import { PersonOutline, WarningAmber } from "@mui/icons-material";
import { useState } from "react";
import { NullableBooleanField } from "../ra-lb-tools/components/nullableBooleanField";
import {
  MyTabbedShowLayout,
  Tab as MyTab,
} from "../ra-lb-tools/components/myTabbedShowLayout";
import { LockerList } from "./lockers";
import { LockerRequestList } from "./lockerRequest";
import { LockerOpenRequestList } from "./lockerOpenRequest";
import { LockerUsageEventList } from "./billing/lockerUsageEvent";
import { MyDateField } from "../ra-lb-tools/components/myDateField";
import { Debug } from "../ra-lb-tools/components/debug";
import { KeycloakLink } from "./misc/keycloakLink";
import { UserRoleField, UserRoleInput } from "./userRoles";
import {
  OrganizationMemberData,
  UserOrganizationMember,
} from "./billing/organizationMembers";
import { StringArrayField } from "../ra-lb-tools/components/StringArrayField";

export const UserIcon = PersonOutline;

const roles = {
  SUPERUSER: 1,
  SERVICE_PROVIDER: 2,
  TECHNICIAN: 3,
  ORG_MEMBER: 4,
};

const rowStyle = (record, palette, currentUserId) => {
  const style =
    record.id == currentUserId && Settings.debugging()
      ? {
          backgroundColor: palette.specialColors.highlight,
        }
      : {};

  return style;
};

const userFilters = [
  <TextInput label="Email" source="email" alwaysOn key={1} />,
  <NullableBooleanField source="enabled" label="Is active" key={2} alwaysOn />,
  <NullableBooleanField source="isServiceAccount" key={3} alwaysOn />,
];

const TowersOwned = ({ source }) => {
  const record = useRecordContext();
  if (!record) return null;
  if (!record[source]) return null;

  const retired = source.endsWith("Retired");

  const onClick = (e) => e.stopPropagation();
  const to = `/users/${record.id}/show/towers` + (retired ? "-retired" : "");

  return (
    <Box sx={{ display: "flex" }}>
      <Link
        to={to}
        onClick={onClick}
        style={sxSpot({ bg: retired ? "red" : "green" })}
      >
        <TextField source={source} />
      </Link>
      {get(record, "attributes.canOwnTowers") ? (
        ""
      ) : (
        <Box title={`User #${record.id} can not own towers`}>
          <WarningAmber sx={{ color: "red" }} />
        </Box>
      )}
    </Box>
  );
};

export const UserList = (props) => {
  const [theme] = useTheme();
  const palette = theme.palette;
  const currentUserId = get(useGetIdentity(), "data.id");

  return (
    <MyList
      {...props}
      resource="users"
      filters={userFilters}
      dataGridProps={{
        rowStyle: (record) => rowStyle(record, palette, currentUserId),
        omit: ["id"],
      }}
    >
      <TextField source="fullName" sortable={false} />
      <EmailField source="email" sortable={false} />
      <BooleanField source="emailValidated" sortable={false} />
      <BooleanField source="isActive" sortable={false} />
      <UserRoleField source="role" />
      <BooleanField
        source="attributes.isServiceAccount"
        label="Is service account"
        sortable={false}
      />
      <BooleanField
        source="attributes.canOwnTowers"
        label="Can own towers"
        sortable={false}
      />
      <TowersOwned
        label="Towers Owned Active"
        source={"towersOwned"}
        sortable={false}
      />
      <TowersOwned
        label="Towers Owned Retired"
        source={"towersOwnedRetired"}
        sortable={false}
      />
      <UserOrganizationMember
        label="Organization"
        source="organizationMember"
      />
      <OrganizationField source="organizationServiced" sortable={false} />
      <OrganizationField source="organizationOwned" sortable={false} />
      <TextField source="status" sortable={false} />
      <MyDateField source="createdAt" sortable={false} />
      <StringArrayField
        source="orgRoles"
        sortable={false}
        can={{ roles: ["-superuser"] }}
      />
      <Box source="id" sortable={false}>
        <TextField source="id" sortable={false} />
        <Debug>
          <KeycloakLink id="id" entity={"users"} />
        </Debug>
      </Box>
    </MyList>
  );
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export const UserPage = () => {
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <ListHeader resource={"users"} />
        <Tabs value={value} onChange={handleChange}>
          <Tab label="Users" {...a11yProps(0)} />
          <Tab label="Service Accounts" {...a11yProps(1)} />
          <Tab label="Inactive Users" {...a11yProps(2)} />
          <Tab label="All" {...a11yProps(3)} />
        </Tabs>
      </Box>
      {value === 0 && (
        <UserList
          noHeaders
          filter={{ isServiceAccount: false, enabled: true }}
        />
      )}
      {value === 1 && (
        <UserList noHeaders filter={{ isServiceAccount: true }} />
      )}
      {value === 2 && (
        <UserList
          noHeaders
          filter={{ isServiceAccount: false, enabled: false }}
        />
      )}
      {value === 3 && <UserList noHeaders />}
    </Box>
  );
};

export const UserEdit = (props) => {
  return (
    <Edit
      {...props}
      formProps={{
        mode: "onBlur",
        reValidateMode: "onBlur",
        validate: validateUserUpdate,
      }}
    >
      <TextInput source="email" />
      <BooleanInput source="emailValidated" />
      <TextInput source="firstName" />
      <TextInput source="lastName" />
      <PasswordInput source="password" />
      <BooleanInput source="isActive" />
      <BooleanInput
        source="attributes.isServiceAccount"
        label="Is service account"
      />
      <BooleanInput source="attributes.canOwnTowers" label="Can own towers" />
      <UserRoleInput source="role" />
    </Edit>
  );
};

export const UserCreate = (props) => {
  return (
    <Create
      {...props}
      formProps={{
        mode: "onBlur",
        reValidateMode: "onBlur",
        validate: (values) => validateUserCreation(values, roles),
      }}
    >
      <TextInput source="email" />
      <BooleanInput source="emailValidated" />
      <TextInput source="firstName" />
      <TextInput source="lastName" />
      <PasswordInput source="password" />
      <BooleanInput source="isActive" />
      <BooleanInput
        source="attributes.isServiceAccount"
        label="Is service account"
      />
      <BooleanInput source="attributes.canOwnTowers" label="Can own towers" />
      <UserRoleInput source="role" />
    </Create>
  );
};

export const UserShow = () => {
  const id = useParams()["id"];

  return (
    <MyShow>
      <MyTabbedShowLayout>
        <MyTab label="Summary" can={true}>
          <RecordSummary sx={{ justifyContent: "start" }}>
            <LabeledColumn>
              <Box source="id">
                <TextField source="id" />
                <Debug>
                  <KeycloakLink id="id" entity={"users"} />
                </Debug>
              </Box>
              <TextField source="fullName" />
              <EmailField source="email" />
              <BooleanField source="emailValidated" />
              <TextField source="status" />
              <MyDateField source="createdAt" />
            </LabeledColumn>
            <LabeledColumn>
              <BooleanField source="isActive" />
              <BooleanField
                source="attributes.isServiceAccount"
                label="Is service account"
              />
              <BooleanField
                source="attributes.canOwnTowers"
                label="Can own towers"
              />
              <UserRoleField source="role" />
              <UserOrganizationMember
                source="organizationMember"
                label="Organization"
              />
              <OrganizationField source="organizationServiced" />
              <OrganizationField source="organizationOwned" />
              <StringArrayField
                source="orgRoles"
                sortable={false}
                can={{ roles: ["-superuser"] }}
              />
            </LabeledColumn>
          </RecordSummary>
        </MyTab>
        <MyTab
          label="Towers"
          path="towers"
          can={[
            { action: "list", resource: "towers", filters: ["ownerId"] },
            { roles: ["superuser"] },
          ]}
        >
          <TowerList filter={{ ownerId: id }} filters={null} />
        </MyTab>
        <MyTab
          label="Retired Towers"
          path="towers-retired"
          can={[
            {
              action: "list",
              resource: "towers",
              filters: ["ownerId", "statusId"],
            },
            { roles: ["superuser"] },
          ]}
        >
          <TowerList filter={{ ownerId: id, statusId: 4 }} filters={null} />
        </MyTab>
        <MyTab
          label="Reservations"
          path="reservations"
          can={{
            action: "list",
            resource: "reservations",
            filters: ["userId"],
          }}
        >
          <ReservationList filter={{ userId: id }} />
        </MyTab>
        <MyTab
          label="Assigned Lockers"
          path="assigned-lockers"
          can={{
            action: "list",
            resource: "lockers",
            filters: ["assignedUserId"],
          }}
        >
          <LockerList filter={{ assignedUserId: id }} />
        </MyTab>
        <MyTab
          label="Locker Open Request"
          path="locker-open-request"
          can={{
            action: "list",
            resource: "locker-open-requests",
            filters: ["userId"],
          }}
        >
          <LockerOpenRequestList filter={{ userId: id }} />
        </MyTab>
        <MyTab
          label="Active Locker Usage"
          path="active-locker-usage"
          can={{
            action: "list",
            resource: "locker-usage",
            filters: ["userId"],
          }}
        >
          <ActiveLockerUsageList filter={{ userId: id }} />
        </MyTab>
        <MyTab
          label="Locker Usage"
          path="locker-usage"
          can={{
            action: "list",
            resource: "locker-usage",
            filters: ["userId"],
          }}
        >
          <LockerUsageList filter={{ userId: id }} />
        </MyTab>
        <MyTab
          label="Locker Usage Events"
          path="locker-usage-events"
          can={{
            action: "list",
            resource: "locker-usage-events",
            filters: ["userId"],
          }}
        >
          <LockerUsageEventList filter={{ userId: id }} />
        </MyTab>
        <MyTab
          label="Open Locker Token"
          path="open-locker-tokens"
          can={{
            action: "list",
            resource: "open-locker-tokens",
            filters: ["userId"],
          }}
        >
          <OpenLockerTokenList filter={{ userId: id }} />
        </MyTab>
        <MyTab
          label="Systems"
          path="systems"
          can={{ action: "list", resource: "systems", filters: ["ownerId"] }}
        >
          <SystemList filter={{ ownerId: id }} />
        </MyTab>
        <MyTab
          label="Locations"
          path="locations"
          can={{ action: "list", resource: "locations", filters: ["ownerId"] }}
        >
          <LocationList filter={{ ownerId: id }} />
        </MyTab>
        <MyTab
          label="Owned Organizations"
          path="owned-organizations"
          can={{
            action: "list",
            resource: "organizations",
            filters: ["accountOwnerId"],
          }}
        >
          <OrganizationList filter={{ accountOwnerId: id }} />
        </MyTab>
        <MyTab
          label="Serviced Organizations"
          path="serviced-organizations"
          can={{
            action: "list",
            resource: "organizations",
            filters: ["serviceAccountId"],
          }}
        >
          <OrganizationList filter={{ serviceAccountId: id }} />
        </MyTab>
        <MyTab
          label="Federation Locker Requests"
          path="federation-locker-requests"
          can={{
            action: "list",
            resource: "locker-request",
            filters: ["userId"],
          }}
        >
          <LockerRequestList filter={{ userId: id }} />
        </MyTab>
        <MyTab
          label="Member of Organization"
          path="member-organization"
          can={{
            action: "list",
            resource: "organization-members",
            filters: ["userId"],
          }}
        >
          <OrganizationMemberData filter={{ userId: id }} />
        </MyTab>
      </MyTabbedShowLayout>
    </MyShow>
  );
};

export const UserInput = (props) => {
  return (
    <MyReferenceInput reference="users" primaryField={"email"} {...props} />
  );
};

export const UserField = (props) => {
  return (
    <MyReferenceField
      reference="users"
      primaryField={["fullName", "email"]}
      {...props}
    />
  );
};
